// src/services/announcements.js
//
// Rules for home page announcements (UI revamp, 2026-10-30). Pure: no
// Firebase (I/O is announcementService.js). The Firestore rules enforce the
// same limits and who-can-post (firestore.rules "announcements"); these mirror
// them so the page can explain a problem before anything is sent.
//
// Audience ("scope"):
//   all    everyone, every company      owners
//   tenant one company                  owners + that company's admins
//   site   one site                     + that site's admins
// Nothing is ever deleted or hidden automatically: a post with no end date
// shows until an admin removes it; one past its "Show until" date stops
// showing but is kept; removing stamps removedAt and keeps the record.

export const LIMITS = Object.freeze({ title: 80, message: 1000 });

const str = (v) => (typeof v === "string" ? v : "");

/** Firestore Timestamp | Date | ms | null -> ms or null. */
export const toMs = (v) => {
  if (v == null) return null;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const ymdParts = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str(ymd));
  return m ? [Number(m[1]), Number(m[2]) - 1, Number(m[3])] : null;
};
/** "2026-11-05" -> that day 00:00 local; "" -> null. */
export const dayStart = (ymd) => {
  const p = ymdParts(ymd);
  return p ? new Date(p[0], p[1], p[2], 0, 0, 0, 0) : null;
};
/** "2026-11-05" -> that day 23:59:59.999 local (shows through the whole day). */
export const dayEnd = (ymd) => {
  const p = ymdParts(ymd);
  return p ? new Date(p[0], p[1], p[2], 23, 59, 59, 999) : null;
};

// ---- Who sees it (2026-11-02: everyone / one company / some of its sites) ----

const isTenantAdminOf = (access, tenantId) =>
  !!access && (access.isOwner || (typeof access.isTenantAdmin === "function" && access.isTenantAdmin(tenantId)));
const isSiteAdminOf = (access, tenantId, siteId) =>
  isTenantAdminOf(access, tenantId) || (typeof access?.roleFor === "function" && access.roleFor(tenantId, siteId) === "admin");

/** Owners can post to everyone at every company. */
export const canPostToEveryone = (access) => !!access?.isOwner;

/**
 * The companies this person can post to, as { id, label, whole }: owners get
 * every company; others the companies they're company admin of or admin of
 * any site in. `whole` = may post to everyone at that company.
 */
export const postableCompanies = (access, tenants) => {
  if (!access) return [];
  return (tenants || [])
    .filter((t) => {
      if (isTenantAdminOf(access, t.id)) return true;
      const sites = typeof access.sitesForTenant === "function" ? access.sitesForTenant(t.id) : [];
      return sites.some((s) => access.roleFor(t.id, s) === "admin");
    })
    .map((t) => ({ id: t.id, label: t.displayName || t.name || t.id, whole: isTenantAdminOf(access, t.id) }));
};

/** The sites of a company this person can post to (all of them for company admins and owners). */
export const postableSites = (access, tenantId, sites) =>
  (sites || []).filter((s) => isSiteAdminOf(access, tenantId, s.id)).map((s) => ({ id: s.id, label: s.name || s.id }));

/** An empty audience choice. */
export const emptyAudience = () => ({ everyone: false, companies: {} });

/**
 * Toggle one choice in the audience (multi-select). `everyone` covers all, so
 * ticking it clears the rest; a whole company covers its sites, so ticking it
 * clears that company's site ticks.
 */
export const toggleAudience = (aud, { kind, companyId, siteId }) => {
  const a = { everyone: !!aud.everyone, companies: { ...(aud.companies || {}) } };
  if (kind === "everyone") return a.everyone ? emptyAudience() : { everyone: true, companies: {} };
  const c = { whole: false, siteIds: [], ...(a.companies[companyId] || {}) };
  if (kind === "company") {
    a.companies[companyId] = c.whole ? { whole: false, siteIds: [] } : { whole: true, siteIds: [] };
  } else if (kind === "site") {
    const has = c.siteIds.includes(siteId);
    a.companies[companyId] = { whole: false, siteIds: has ? c.siteIds.filter((s) => s !== siteId) : [...c.siteIds, siteId] };
  }
  return a;
};

/**
 * The audience picked on the form (multi-select) -> the posts to create, one
 * per target: [{ scope, tenantId, siteId }]. Several targets are posted
 * together (linked by a groupId) and shown/removed as one. Or { error } if a
 * choice isn't allowed or nothing is picked.
 *   aud = { everyone, companies: { [tenantId]: { whole, siteIds: [] } } }
 */
export const audienceTargets = (aud, access) => {
  const a = aud || emptyAudience();
  if (a.everyone) {
    return canPostToEveryone(access) ? [{ scope: "all", tenantId: "", siteId: "" }] : { error: "Only owners can post to everyone." };
  }
  const targets = [];
  for (const [tenantId, c] of Object.entries(a.companies || {})) {
    if (!c) continue;
    if (c.whole) {
      if (!isTenantAdminOf(access, tenantId)) return { error: "You can post to that company's sites, not to the whole company." };
      targets.push({ scope: "tenant", tenantId, siteId: "" });
      continue;
    }
    for (const siteId of [...new Set((c.siteIds || []).filter(Boolean))]) {
      if (!isSiteAdminOf(access, tenantId, siteId)) return { error: "You can't post to one of those sites." };
      targets.push({ scope: "site", tenantId, siteId });
    }
  }
  return targets.length ? targets : { error: "Choose who should see it." };
};

/** How many posts a choice makes, in words, for the form: "Everyone", "2 sites", ... */
export const audienceSummary = (aud, { companyLabel = (id) => id, siteLabel = (t, s) => s } = {}) => {
  const a = aud || emptyAudience();
  if (a.everyone) return "Everyone at all companies";
  const parts = [];
  Object.entries(a.companies || {}).forEach(([tenantId, c]) => {
    if (c?.whole) parts.push(`everyone at ${companyLabel(tenantId)}`);
    else (c?.siteIds || []).forEach((s) => parts.push(siteLabel(tenantId, s)));
  });
  return parts.join(", ");
};

/** May this person remove this post? Same rule as posting to its audience. */
export const canManage = (post, access) => {
  if (!post || !access) return false;
  if (access.isOwner) return true;
  const isTA = typeof access.isTenantAdmin === "function" && access.isTenantAdmin(post.tenantId);
  if (post.scope === "tenant") return isTA;
  if (post.scope === "site") {
    return isTA || (typeof access.roleFor === "function" && access.roleFor(post.tenantId, post.siteId) === "admin");
  }
  return false;
};

/**
 * Check the post form -> { ok, data, targets } (the fields every post shares,
 * and one audience per post to create) or { ok: false, errors: { field: message } }.
 */
export const validatePost = (form, access) => {
  const f = form || {};
  const errors = {};
  const title = str(f.title).trim();
  const message = str(f.message).trim();
  if (!title) errors.title = "Add a title.";
  else if (title.length > LIMITS.title) errors.title = `Titles can be at most ${LIMITS.title} characters.`;
  if (!message) errors.message = "Add a message.";
  else if (message.length > LIMITS.message) errors.message = `Messages can be at most ${LIMITS.message} characters.`;
  if (!["normal", "important"].includes(f.importance)) errors.importance = "Choose Normal or Important.";
  const targets = audienceTargets(f.audience, access);
  if (!Array.isArray(targets)) errors.audience = targets.error;
  const showFrom = f.showFrom ? dayStart(f.showFrom) : null;
  const showUntil = f.showUntil ? dayEnd(f.showUntil) : null;
  if (f.showFrom && !showFrom) errors.showFrom = "That date isn't valid.";
  if (f.showUntil && !showUntil) errors.showUntil = "That date isn't valid.";
  if (showFrom && showUntil && showUntil <= showFrom) errors.showUntil = "“Show until” must be after “Show from”.";
  if (Object.keys(errors).length) return { ok: false, errors };
  return {
    ok: true,
    data: { title, message, importance: f.importance, showFrom, showUntil },
    targets,
  };
};

/**
 * Posts made together for several sites share a groupId; show and remove them
 * as one. -> [{ key, posts }] in the order first seen.
 */
export const groupPosts = (posts) => {
  const groups = new Map();
  (posts || []).forEach((p) => {
    const key = p.groupId || p.id;
    if (!groups.has(key)) groups.set(key, { key, posts: [] });
    groups.get(key).posts.push(p);
  });
  return [...groups.values()];
};

// Who posted / removed it: the login email (2026-11-02). Posts made before that
// carried a typed name instead, so fall back to it.
export const posterOf = (post) => str(post && (post.postedByEmail || post.postedByName));
export const removerOf = (post) => str(post && (post.removedByEmail || post.removedByName));

/** When the post became (or becomes) visible: its show-from date, else when it was posted. */
export const visibleSince = (post) => Math.max(toMs(post.postedAt) || 0, toMs(post.showFrom) || 0);

export const isRemoved = (post) => toMs(post.removedAt) != null;
export const isScheduled = (post, now) => !isRemoved(post) && (toMs(post.showFrom) || 0) > now;
export const isEnded = (post, now) => toMs(post.showUntil) != null && toMs(post.showUntil) <= now;
/** Showing right now: not removed, started, not ended. */
export const isShowing = (post, now) => !isRemoved(post) && !isScheduled(post, now) && !isEnded(post, now);

const byImportanceThenNewest = (a, b) =>
  Number(b.importance === "important") - Number(a.importance === "important") || visibleSince(b) - visibleSince(a);

/** What a person's home page shows: posts showing now, Important first, newest first. */
export const forHome = (posts, now) => dedupe(posts).filter((p) => isShowing(p, now)).sort(byImportanceThenNewest);

/** Is this post new since the person was last shown the list? (Never shown = all new.) */
export const isNew = (post, seenMs) => seenMs == null || visibleSince(post) > seenMs;

/** The admin's "Showing now" tab: posts they manage that are showing or scheduled. */
export const forAdmin = (posts, access, now) =>
  dedupe(posts)
    .filter((p) => canManage(p, access) && !isRemoved(p) && !isEnded(p, now))
    .sort(byImportanceThenNewest);

/** Split for display: { showing, scheduled } (scheduled: soonest start first). */
export const splitShowingScheduled = (posts, now) => ({
  showing: posts.filter((p) => !isScheduled(p, now)),
  scheduled: posts.filter((p) => isScheduled(p, now)).sort((a, b) => toMs(a.showFrom) - toMs(b.showFrom)),
});

/** When a post left the home page: removed, or its "Show until" passed (ms). */
export const endedAt = (post) => (isRemoved(post) ? toMs(post.removedAt) : toMs(post.showUntil));

/**
 * The admin's "History" tab: posts they manage that were removed or have
 * ended, newest first. Read-only; nothing here is ever deleted.
 */
export const forHistory = (posts, access, now) =>
  dedupe(posts)
    .filter((p) => canManage(p, access) && (isRemoved(p) || isEnded(p, now)))
    .sort((a, b) => (endedAt(b) || 0) - (endedAt(a) || 0));

const fmtDate = (ms) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

/** "Removed Nov 3, 2026 by pat@x.com" or "Ended Nov 9, 2026". */
export const historyText = (post) => {
  if (isRemoved(post)) {
    const by = removerOf(post);
    return `Removed ${fmtDate(toMs(post.removedAt))}${by ? ` by ${by}` : ""}`;
  }
  return `Ended ${fmtDate(toMs(post.showUntil))}`;
};

/** "Posted Nov 1, 2026 by pat@x.com". */
export const postedText = (post) => {
  const by = posterOf(post);
  const at = toMs(post.postedAt);
  return `Posted${at ? ` ${fmtDate(at)}` : ""}${by ? ` by ${by}` : ""}`;
};

/** "Scheduled · from Nov 5, 2026", "Showing · until Nov 9, 2026", "Showing". */
export const statusText = (post, now) => {
  if (isScheduled(post, now)) return `Scheduled · from ${fmtDate(toMs(post.showFrom))}`;
  const until = toMs(post.showUntil);
  return until != null ? `Showing · until ${fmtDate(until)}` : "Showing";
};

/**
 * Who a post (or a group of posts made together) is for, in words:
 * "Everyone", "Everyone at Lung Innovations", "Dubois only", "Dubois, Ohio only".
 */
export const audienceText = (postOrPosts, { tenantsMeta = [], sitesMeta = [] } = {}) => {
  const posts = Array.isArray(postOrPosts) ? postOrPosts : [postOrPosts];
  if (posts.some((p) => p.scope === "all")) return "Everyone";
  const companyName = (id) => {
    const t = tenantsMeta.find((x) => x.id === id);
    return (t && (t.displayName || t.name)) || id;
  };
  const siteName = (p) => {
    const s = sitesMeta.find((x) => x.id === p.siteId && (!x.tenantId || x.tenantId === p.tenantId));
    return (s && s.name) || p.siteId;
  };
  const parts = posts.map((p) => (p.scope === "tenant" ? `Everyone at ${companyName(p.tenantId)}` : siteName(p)));
  const sitesOnly = posts.every((p) => p.scope === "site");
  return sitesOnly ? `${parts.join(", ")} only` : parts.join(", ");
};

export function dedupe(posts) {
  const seen = new Map();
  (posts || []).forEach((p) => p && p.id && seen.set(p.id, p));
  return [...seen.values()];
}
